"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { auth } from "../Firebase/firebase-config"
import { onAuthStateChanged } from "firebase/auth"
import { deleteUser } from "firebase/auth"
import AuthService from "./AuthService"
import "./ProfileCompletionScreen.css"
import { useAccount } from "../context/AccountContext"


const ProfileCompletionScreen = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const { reload } = useAccount();
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [notification, setNotification] = useState({ message: "", type: "" })

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    gstin: "",
    pan: "",
    company_name: "",
    type: "main",
    username: "",
  })

  const [currentUser, setCurrentUser] = useState(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user)
        if (user.email) {
          setFormData((prev) => ({ ...prev, email: user.email }))
        }
      } else {
        navigate("/login")
      }
    })

    return () => unsubscribe()
  }, [navigate])
const handleCancel = async () => {
  try {
    const user = auth.currentUser

    if (user) {
      await deleteUser(user) // 🔥 removes phone auth user completely
    }

    navigate("/") // go to home page
  } catch (error) {
    console.error("Cancel error:", error)

    if (error.code === "auth/requires-recent-login") {
      showNotification("Session expired. Please login again.", "error")
    } else {
      showNotification("Something went wrong while cancelling.", "error")
    }
  }
}

  const showNotification = (message, type = "info") => {
    setNotification({ message, type })
    setTimeout(() => setNotification({ message: "", type: "" }), 5000)
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    if (field === "gstin" && value.length === 15) {
      setFormData((prev) => ({ ...prev, pan: value.substring(2, 12) }))
    } else if (field === "gstin" && value.length < 15) {
      setFormData((prev) => ({ ...prev, pan: "" }))
    }
  }

  const handleCompleteProfile = async (e) => {
    e.preventDefault()

    if (
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.company_name ||
      !formData.username ||
      !agreedToTerms
    ) {
      showNotification("Please fill all required fields and agree to terms", "error")
      return
    }

    if (!AuthService.isValidEmail(formData.email)) {
      showNotification("Please enter a valid email address", "error")
      return
    }

    if (!AuthService.isStrongPassword(formData.password)) {
      showNotification("Password must include uppercase, lowercase, number, and special character (8–16 characters)",
    "error")
      return
    }

    if (formData.password !== formData.confirmPassword) {
      showNotification("Passwords do not match", "error")
      return
    }

    if (formData.gstin && !AuthService.isValidGSTIN(formData.gstin)) {
      showNotification("Invalid GSTIN format", "error")
      return
    }

    if (formData.pan && !AuthService.isValidPAN(formData.pan)) {
      showNotification("Invalid PAN format", "error")
      return
    }

    setIsLoading(true)

    try {
      await AuthService.linkEmailToPhoneAccount(formData.email.trim(), formData.password)

      const profileData = {
        gstin: formData.gstin.trim(),
        pan: formData.pan.trim(),
        company_name: formData.company_name.trim(),
        phone: currentUser?.phoneNumber?.replace(/^\+91/, "") || "",
        email: formData.email.trim(),
        type:'main',
        username: formData.username.trim(),
      }

      await AuthService.saveUserProfile(profileData)
      await reload();
      showNotification("✅ Profile completed successfully!", "success")

      setTimeout(() => {
        navigate("/success")
      }, 1500)
    } catch (error) {
      console.error("Profile completion error:", error)
      showNotification("Failed to complete profile. Please try again.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const renderInputField = ({
    label,
    value,
    onChange,
    placeholder,
    type = "text",
    icon,
    isPassword = false,
    showPassword: showPass,
    onTogglePassword,
    maxLength,
    helperText,
    required = false,
  }) => (
    <div className="input-group">
      <label className="input-label">
        {label} {required && <span className="required">*</span>}
      </label>
      <div className="input-wrapper">
        <div className="input-icon">{icon}</div>
        <input
          type={isPassword ? (showPass ? "text" : "password") : type}
          value={value}
          onChange={(e) => onChange(type === "email" ? e.target.value.toLowerCase() : e.target.value)}
          placeholder={placeholder}
          className="form-input"
          maxLength={maxLength}
          style={isPassword ? { paddingRight: "40px" } : {}}
        />
        {isPassword && (
          <button type="button" className="password-toggle" onClick={onTogglePassword}>
            {showPass ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        )}
      </div>
      {helperText && <div className="helper-text">{helperText}</div>}
    </div>
  )

  return (
    <div className="profile-screen">
      <div className="profile-container">
        <div className="profile-left">
          <div className="brand-section">
            <div className="brand-logo">
              <div className="logo-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <span className="brand-name">Altrange</span>
            </div>
            <h1 className="welcome-title-para">Almost There!</h1>
            <p className="welcome-subtitle-para">
              Complete your profile to access all features and start using our platform.
            </p>
          </div>

          <div className="success-info">
            <div className="success-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <div className="success-content">
              <h3>Phone Verified Successfully!</h3>
              <p>Your phone number has been verified</p>
              {currentUser?.phoneNumber && (
                <div className="phone-info">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  {currentUser.phoneNumber}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="profile-right">
          <div className="profile-card">
            <div className="card-header">
              <h2>Complete Your Profile</h2>
              <p>Fill in your details to finish setting up your account</p>
            </div>
            <form onSubmit={handleCompleteProfile} className="profile-form">
              <div className="form-section">
                <div className="form-grid">
                  {renderInputField({
                    label: "Email Address",
                    value: formData.email,
                    onChange: (value) => handleInputChange("email", value),
                    placeholder: "Enter your email address",
                    type: "email",
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    ),
                    required: true,
                  })}

                  {renderInputField({
                    label: "Password",
                    value: formData.password,
                    onChange: (value) => handleInputChange("password", value),
                    placeholder: "Enter password (6-12 chars)",
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <circle cx="12" cy="16" r="1" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    ),
                    isPassword: true,
                    showPassword: showPassword,
                    onTogglePassword: () => setShowPassword(!showPassword),
                    required: true,
                  })}

                  {renderInputField({
                    label: "Confirm Password",
                    value: formData.confirmPassword,
                    onChange: (value) => handleInputChange("confirmPassword", value),
                    placeholder: "Confirm your password",
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <circle cx="12" cy="16" r="1" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    ),
                    isPassword: true,
                    showPassword: showConfirmPassword,
                    onTogglePassword: () => setShowConfirmPassword(!showConfirmPassword),
                    required: true,
                  })}
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <div className="section-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div>
                    <h3>Personal Details</h3>
                    <p>Tell us about yourself</p>
                  </div>
                </div>

                <div className="form-grid">
                  {renderInputField({
                    label: "Full Name",
                    value: formData.username,
                    onChange: (value) => handleInputChange("username", value),
                    placeholder: "Enter your full name",
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    ),
                    required: true,
                  })}

                  {renderInputField({
                    label: "Company Name",
                    value: formData.company_name,
                    onChange: (value) => handleInputChange("company_name", value),
                    placeholder: "Enter your company name",
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 21h18" />
                        <path d="M5 21V7l8-4v18" />
                        <path d="M19 21V11l-6-4" />
                      </svg>
                    ),
                    required: true,
                  })}
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <div className="section-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </div>
                  <div>
                    <h3>Business Details</h3>
                    <p>Optional business information</p>
                  </div>
                </div>

                <div className="form-grid">
                  {renderInputField({
                    label: "GSTIN",
                    value: formData.gstin,
                    onChange: (value) => handleInputChange("gstin", value.toUpperCase()),
                    placeholder: "Enter GSTIN number",
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                      </svg>
                    ),
                    maxLength: 15,
                    helperText: "PAN will be auto-filled from GSTIN",
                  })}

                  {renderInputField({
                    label: "PAN Number",
                    value: formData.pan,
                    onChange: (value) => handleInputChange("pan", value.toUpperCase()),
                    placeholder: "Enter PAN number",
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                    ),
                    maxLength: 10,
                  })}
                </div>
              </div>

              <div className="terms-section">
                <label className="checkbox-container">
                  <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
                  <span className="checkmark"></span>
                  <span className="terms-text">
                    I agree to the{" "}
                    <Link to="/term&condition" className="terms-link">
                      terms and conditions
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy-policy" className="terms-link">
                      privacy policy
                    </Link>
                    <span className="required"> *</span>
                  </span>
                </label>
              </div>

              <button type="submit" className="complete-button" disabled={isLoading}>
                {isLoading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <>
                    <span>Complete Profile & Continue</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
              <button
  type="button"
  className="cancel-button"
  onClick={handleCancel}
>
  Cancel
</button>

              {notification.message && (
                <div className={`notification ${notification.type}`}>
                  <div className="notification-content">
                    <span>{notification.message}</span>
                  </div>
                </div>
              )}

              <div className="required-note">
                <span className="required">*</span> Required fields
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export const SuccessScreen = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/dashboard")
    }, 1000)

    return () => clearTimeout(timer)
  }, [navigate])

  return (
    <div className="success-screen">
      <div className="success-container">
        <div className="success-card">
          <div className="success-animation">
            <div className="success-icon-large">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
          </div>
          <h1>Welcome to Altrange!</h1>
          <p>Your profile has been completed successfully</p>
          <div className="success-note">You can now login with either email/password or phone/OTP</div>
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Redirecting to Dashboard...</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfileCompletionScreen
